import type { Viewer } from '@puppycad/cad-core';

/**
 * Viewer 初始化完成后的订阅取消函数。
 */
type ViewerCallbackDisposer = () => void;

/**
 * Viewer 初始化完成后的回调函数。
 */
type ViewerReadyCallback = (viewer: Viewer) => void;

/**
 * 全局共享的 Viewer 引用。
 *
 * 说明：Viewer 是 cad-core 暴露的运行时对象，不能放入 Vue 响应式链路；
 * 这里只保存非响应式句柄，供跨组件读取同一个编辑器运行时实例。
 */
let viewerInstance: Viewer | null = null;

/**
 * Viewer 尚未创建时暂存的初始化回调集合。
 *
 * 说明：使用 Set 保证同一个回调不会重复注册，并支持组件卸载时主动取消等待。
 */
const viewerCallbackFns = new Set<ViewerReadyCallback>();

/**
 * 共享 Viewer 访问钩子。
 *
 * @returns Viewer 运行时读写方法。
 */
export function useViewer() {
  /**
   * 注册 Viewer 初始化完成后的回调。
   *
   * 说明：工具栏等子组件可能先于 Viewer 创建完成挂载，必须允许先注册等待回调；
   * 同时组件卸载时需要取消等待，避免 Viewer 后续创建后触发已经失效的组件闭包。
   *
   * @param callback Viewer 创建完成后的回调。
   * @returns 取消等待 Viewer 初始化回调的函数。
   */
  const setViewerCallback = (callback: ViewerReadyCallback): ViewerCallbackDisposer => {
    if (viewerInstance) {
      callback(viewerInstance);
      return () => undefined;
    }

    viewerCallbackFns.add(callback);

    return () => {
      viewerCallbackFns.delete(callback);
    };
  };

  /**
   * 设置共享 Viewer 运行时引用。
   *
   * @param value Viewer 实例。
   */
  const setViewer = (value: Viewer): void => {
    viewerInstance = value;

    viewerCallbackFns.forEach((callback) => {
      callback(value);
    });
    viewerCallbackFns.clear();
  };

  /**
   * 读取当前共享 Viewer 运行时引用。
   *
   * @returns 当前 Viewer 实例；尚未初始化或已清理时返回 null。
   */
  const getViewer = (): Viewer | null => {
    return viewerInstance;
  };

  /**
   * 清理共享 Viewer 运行时引用。
   */
  const clearViewer = (): void => {
    viewerInstance = null;
  };

  return {
    setViewerCallback,
    setViewer,
    getViewer,
    clearViewer,
  };
}
